import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Ionicons } from '@expo/vector-icons';

interface ErrorViewProps {
  message: string;
  onRetry?: () => void;
  icon?: string;
  title?: string;
}

/**
 * ✅ P1-03: Standardized Error View Component
 *
 * Used when:
 * - API call fails
 * - Network error occurs
 * - Data validation fails
 * - Any error that prevents data display
 *
 * Features:
 * - RTL support (Arabic/English)
 * - Dark mode support
 * - Retry button (optional)
 * - Customizable icon and message
 * - Accessible
 */
export const ErrorView: React.FC<ErrorViewProps> = ({
  message,
  onRetry,
  icon = 'alert-circle-outline',
  title,
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isArabic = i18n.language === 'ar';

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.background,
      padding: 24,
    },
    iconContainer: {
      marginBottom: 16,
    },
    title: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.error,
      textAlign: 'center',
      marginBottom: 8,
    },
    message: {
      fontSize: 16,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      marginBottom: 24,
      lineHeight: 24,
    },
    retryButton: {
      backgroundColor: theme.colors.primary,
      paddingHorizontal: 32,
      paddingVertical: 12,
      borderRadius: 8,
      minWidth: 120,
    },
    retryButtonText: {
      color: '#fff',
      fontSize: 16,
      fontWeight: '600',
      textAlign: 'center',
    },
  });

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Ionicons
          name={icon as any}
          size={64}
          color={theme.colors.error}
          accessibilityLabel={t('error_icon')}
        />
      </View>

      {title && <Text style={styles.title}>{title}</Text>}

      <Text style={styles.message}>{message}</Text>

      {onRetry && (
        <TouchableOpacity
          style={styles.retryButton}
          onPress={onRetry}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={t('try_again')}
        >
          <Text style={styles.retryButtonText}>{t('try_again')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

export default ErrorView;
